import { Check, Copy, DownloadSimple } from '@phosphor-icons/react';
import {
  getGetBillingDetailsQueryKey,
  getGetSubscriptionQueryKey,
  getInvoiceUrl,
  useCancelSubscription,
  useChooseSubscription,
  useCreateCheckout,
  useCreatePaymentMethodCheckout,
  useGetBillingDetails,
  useGetSubscription,
  useListPlans,
  useResumeSubscription,
} from '@reelplicate/api-client/billing';
import type {
  CheckoutSessionDto,
  InvoiceDto,
  PaymentMethodDto,
  PlanDto,
  SubscriptionDto,
} from '@reelplicate/api-client/model';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  Badge,
  Button,
  cn,
  ConfirmDialog,
  IconButton,
  Link,
  Modal,
  ModalContent,
  Skeleton,
  toast,
  Well,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useEffectEvent, useId, useRef, useState } from 'react';

import { useCheckout } from '@/lib/billing/use-checkout';

import {
  ListRow,
  SettingsCard,
  SettingsPageLayout,
  SettingsRow,
  SettingsSection,
} from './settings-layout';
import { UsageMeter } from './usage-meter';

// Mock usage from Figma "Settings — Billing & plan" (322:874) until usage
// tracking exists: what's used against the plan's limits.
const VIDEOS_USED = 72;
const SEATS_USED = 3;

/** How long to keep re-reading after a checkout, while the provider's webhook lands. */
const SYNC_MS = 15_000;

const dateFormat = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
const formatDate = (value: string) => dateFormat.format(new Date(value));

/** Subscribed through the provider and not ended: it can be canceled or have its card changed. */
const isLive = (subscription: SubscriptionDto | undefined) =>
  subscription?.paymentsEnabled === true &&
  subscription.status !== 'INCOMPLETE' &&
  subscription.status !== 'CANCELED';

type CheckoutKind = 'subscribe' | 'card';

/**
 * Figma "Settings — Billing & plan" (322:874): the current plan with usage
 * meters, the three plans, payment details and invoices. 960 wide. Plans,
 * the workspace's plan, its card, billing email and invoices come from the
 * API (the payment provider behind it); changing plan keeps the billing
 * period and is prorated. Usage counts are still mock data.
 */
export function BillingPage() {
  const queryClient = useQueryClient();
  const [syncing, setSyncing] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [checkoutKind, setCheckoutKind] = useState<CheckoutKind | null>(null);
  const { data: workspaces } = useListWorkspaces();
  const workspaceId = workspaces?.[0]?.id;
  const { data: plans } = useListPlans();
  // No plan chosen yet is expected (404): the plans section becomes the chooser.
  const { data: subscription, error: noPlan } = useGetSubscription(workspaceId ?? '', {
    query: {
      enabled: Boolean(workspaceId),
      retry: false,
      throwOnError: false,
      refetchInterval: syncing ? 2000 : false,
    },
  });
  const paymentsEnabled = subscription?.paymentsEnabled ?? false;
  const { data: details } = useGetBillingDetails(workspaceId ?? '', {
    query: {
      enabled: Boolean(workspaceId) && paymentsEnabled,
      throwOnError: false,
      refetchInterval: syncing ? 2000 : false,
    },
  });
  const choose = useChooseSubscription();
  const cancel = useCancelSubscription();
  const resume = useResumeSubscription();
  const createCheckout = useCreateCheckout();
  const createCardCheckout = useCreatePaymentMethodCheckout();

  useEffect(() => {
    if (!syncing) return;
    const timer = setTimeout(() => setSyncing(false), SYNC_MS);
    return () => clearTimeout(timer);
  }, [syncing]);

  const loading = !plans || (!subscription && !noPlan);
  const currentIndex = plans?.findIndex((p) => p.id === subscription?.plan) ?? -1;
  const current = plans?.[currentIndex];
  // Trialing now, or about to be: a checkout on a period with a free trial.
  const trial =
    subscription?.status === 'TRIALING' ||
    (subscription?.status === 'INCOMPLETE' &&
      ((subscription.billingPeriod === 'YEARLY'
        ? current?.yearlyTrialDays
        : current?.monthlyTrialDays) ?? 0) > 0);
  const yearly = subscription?.billingPeriod === 'YEARLY';
  const live = isLive(subscription);

  const store = (next: SubscriptionDto) => {
    if (workspaceId)
      queryClient.setQueryData(getGetSubscriptionQueryKey(workspaceId), next);
  };

  const onChoose = async (plan: PlanDto) => {
    if (!workspaceId) return;
    try {
      store(
        await choose.mutateAsync({
          id: workspaceId,
          data: {
            plan: plan.id,
            billingPeriod: subscription?.billingPeriod ?? 'MONTHLY',
          },
        }),
      );
    } catch {
      return; // The failure is already shown as a toast.
    }
    toast.success(`Switched to ${plan.name}`);
  };

  const onCancel = async () => {
    if (!workspaceId) return;
    try {
      store(await cancel.mutateAsync({ id: workspaceId }));
    } catch {
      return;
    }
    toast.success('Your plan is canceled. It stays active until the end of the period.');
  };

  const onResume = async () => {
    if (!workspaceId) return;
    try {
      store(await resume.mutateAsync({ id: workspaceId }));
    } catch {
      return;
    }
    toast.success('Your plan will renew as usual');
  };

  const onCheckoutCompleted = () => {
    setCheckoutKind(null);
    toast.success(
      checkoutKind === 'card'
        ? 'Payment method updated'
        : trial
          ? 'Your trial has started'
          : 'Your plan has started',
    );
    if (!workspaceId) return;
    setSyncing(true);
    void queryClient.invalidateQueries({
      queryKey: getGetSubscriptionQueryKey(workspaceId),
    });
    void queryClient.invalidateQueries({
      queryKey: getGetBillingDetailsQueryKey(workspaceId),
    });
  };

  const onDownload = async (invoice: InvoiceDto) => {
    if (!workspaceId) return;
    try {
      const { url } = await getInvoiceUrl(workspaceId, invoice.id);
      window.open(url, '_blank', 'noopener');
    } catch {
      toast.error('Couldn’t get that invoice. Try again.');
    }
  };

  return (
    <SettingsPageLayout
      wide
      title="Billing & plan"
      description={
        current
          ? `You’re on ${current.name}. Change plans any time — we prorate the rest of the period.`
          : 'Choose a plan for your workspace.'
      }
    >
      {loading ? (
        <Skeleton shape="rectangle" shimmer className="h-[218px] rounded-lg" />
      ) : (
        subscription &&
        current && (
          <CurrentPlan
            subscription={subscription}
            plan={current}
            pending={cancel.isPending || resume.isPending}
            onCancel={() => setConfirmingCancel(true)}
            onResume={() => void onResume()}
            onSubscribe={() => setCheckoutKind('subscribe')}
          />
        )
      )}

      <SettingsSection
        title="Plans"
        description={`Prices are per workspace, billed ${yearly ? 'yearly' : 'monthly'} in USD.`}
      >
        <div id="plans" className="flex flex-col gap-4 md:flex-row md:items-stretch">
          {loading
            ? [0, 1, 2].map((i) => (
                <Skeleton
                  key={i}
                  shape="rectangle"
                  shimmer
                  className="h-[440px] flex-1 rounded-xl"
                />
              ))
            : plans.map((plan, index) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  yearly={yearly}
                  current={index === currentIndex}
                  recommended={current !== undefined && index === currentIndex + 1}
                  cta={
                    !current
                      ? `Choose ${plan.name}`
                      : index === currentIndex
                        ? 'Your current plan'
                        : `${index > currentIndex ? 'Upgrade' : 'Downgrade'} to ${plan.name}`
                  }
                  disabled={choose.isPending}
                  onChoose={() => void onChoose(plan)}
                />
              ))}
        </div>
      </SettingsSection>

      <SettingsSection title="Payment" description="Receipts go to the billing email.">
        <SettingsCard>
          {!subscription || !paymentsEnabled ? (
            <SettingsRow
              label="No payment method"
              description={
                subscription
                  ? 'Payments are off on this server.'
                  : 'Choose a plan to add one.'
              }
            />
          ) : !details ? (
            <div className="p-4">
              <Skeleton shape="rectangle" shimmer className="h-12" />
            </div>
          ) : (
            <>
              <SettingsRow
                label={
                  details.paymentMethod
                    ? paymentMethodLabel(details.paymentMethod)
                    : 'No payment method yet'
                }
                description={
                  details.paymentMethod
                    ? paymentMethodDetail(details.paymentMethod)
                    : trial
                      ? 'Add one to start your trial.'
                      : 'Add one to start your plan.'
                }
                control={
                  live ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCheckoutKind('card')}
                    >
                      Update
                    </Button>
                  ) : undefined
                }
              />
              <SettingsRow
                label="Billing email"
                description="Invoices and payment problems go here."
                control={
                  details.billingEmail ? (
                    <>
                      <span className="text-data-md text-text-primary">
                        {details.billingEmail}
                      </span>
                      <IconButton
                        icon={Copy}
                        size="sm"
                        variant="ghost"
                        aria-label="Copy billing email"
                        onClick={() => {
                          void navigator.clipboard?.writeText(details.billingEmail ?? '');
                          toast.success('Billing email copied');
                        }}
                      />
                    </>
                  ) : (
                    <span className="text-body-md text-text-tertiary">Not set yet</span>
                  )
                }
              />
            </>
          )}
        </SettingsCard>
      </SettingsSection>

      <SettingsSection title="Invoices">
        <SettingsCard>
          {details && details.invoices.length > 0 ? (
            details.invoices.map((invoice) => (
              <ListRow
                key={invoice.id}
                title={formatDate(invoice.date)}
                meta={invoice.description}
              >
                <span className="text-data-md text-text-primary">
                  {formatMoney(invoice.amount, invoice.currency)}
                </span>
                <InvoiceBadge status={invoice.status} />
                <IconButton
                  icon={DownloadSimple}
                  size="sm"
                  variant="ghost"
                  aria-label={`Download invoice for ${formatDate(invoice.date)}`}
                  onClick={() => void onDownload(invoice)}
                />
              </ListRow>
            ))
          ) : (
            <SettingsRow
              label="No invoices yet"
              description={
                trial
                  ? 'Your first one arrives when the trial ends.'
                  : 'Your first one arrives after your first payment.'
              }
            />
          )}
        </SettingsCard>
      </SettingsSection>

      <ConfirmDialog
        open={confirmingCancel}
        onOpenChange={setConfirmingCancel}
        title={`Cancel ${current?.name ?? 'your plan'}?`}
        description={
          subscription?.currentPeriodEnd
            ? `You keep everything until ${formatDate(subscription.currentPeriodEnd)}, then the workspace stops publishing. You can resume any time before then.`
            : 'The workspace stops publishing at the end of the period. You can resume any time before then.'
        }
        confirmLabel="Cancel plan"
        cancelLabel="Keep plan"
        onConfirm={() => void onCancel()}
      />

      <CheckoutModal
        open={checkoutKind !== null}
        onOpenChange={(open) => !open && setCheckoutKind(null)}
        title={checkoutKind === 'card' ? 'Update payment method' : 'Add a payment method'}
        description={
          checkoutKind === 'card'
            ? 'Future charges go to the new card.'
            : trial
              ? 'You won’t be charged until your trial ends.'
              : 'Your first period is charged now.'
        }
        createSession={() =>
          checkoutKind === 'card'
            ? createCardCheckout.mutateAsync({ id: workspaceId ?? '' })
            : createCheckout.mutateAsync({ id: workspaceId ?? '' })
        }
        onCompleted={onCheckoutCompleted}
      />
    </SettingsPageLayout>
  );
}

/** The workspace's plan, its price and period, and usage against its limits. */
function CurrentPlan({
  subscription,
  plan,
  pending,
  onCancel,
  onResume,
  onSubscribe,
}: {
  subscription: SubscriptionDto;
  plan: PlanDto;
  pending: boolean;
  onCancel: () => void;
  onResume: () => void;
  onSubscribe: () => void;
}) {
  const period = subscription.billingPeriod === 'YEARLY' ? 'year' : 'month';
  const { status, trialEndsAt, currentPeriodEnd, cancelAtPeriodEnd } = subscription;
  const when =
    cancelAtPeriodEnd && currentPeriodEnd
      ? ` · ends on ${formatDate(currentPeriodEnd)}`
      : status === 'TRIALING' && trialEndsAt
        ? ` · free trial ends on ${formatDate(trialEndsAt)}`
        : status === 'ACTIVE' && currentPeriodEnd
          ? ` · renews on ${formatDate(currentPeriodEnd)}`
          : '';
  return (
    <section
      aria-label="Current plan"
      className="bg-bg-surface border-border-subtle flex flex-col gap-6 rounded-lg border p-6"
    >
      <div className="flex items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-heading-h2 text-text-primary">{plan.name}</p>
            <Badge tone="brand">Current plan</Badge>
            {status === 'PAST_DUE' && <Badge tone="danger">Payment failed</Badge>}
            {status === 'CANCELED' && <Badge tone="danger">Canceled</Badge>}
            {status === 'PAUSED' && <Badge tone="warning">Paused</Badge>}
            {cancelAtPeriodEnd && status !== 'CANCELED' && (
              <Badge tone="warning">Canceling</Badge>
            )}
          </div>
          <p className="text-body-md text-text-secondary">
            ${subscription.price} per {period}
            {when}
          </p>
        </div>
        {!subscription.paymentsEnabled ? null : status === 'INCOMPLETE' ? (
          <Button onClick={onSubscribe}>Add payment method</Button>
        ) : status === 'CANCELED' ? null : cancelAtPeriodEnd ? (
          <Button variant="outline" loading={pending} onClick={onResume}>
            Resume plan
          </Button>
        ) : (
          <Button variant="ghost" disabled={pending} onClick={onCancel}>
            Cancel plan
          </Button>
        )}
      </div>
      {status === 'PAST_DUE' && (
        <Well tone="danger" title="Your last payment didn’t go through">
          We’ll retry the card over the next few days. Update it below to avoid losing
          access.
        </Well>
      )}
      <div className="flex flex-col gap-6 sm:flex-row sm:gap-8">
        <UsageMeter
          label="Videos this month"
          used={VIDEOS_USED}
          limit={plan.videosPerMonth}
          caption="Resets on 1 Oct"
        />
        <UsageMeter
          label="Seats"
          used={SEATS_USED}
          limit={plan.seats}
          caption="All seats used — upgrade to invite more people"
          action={
            <Link href="#plans" size="sm">
              Upgrade
            </Link>
          }
        />
      </div>
    </section>
  );
}

/**
 * The payment provider's inline checkout in a modal: subscribing after the
 * fact, or replacing the card. The session is created when it opens.
 */
function CheckoutModal({
  open,
  onOpenChange,
  title,
  description,
  createSession,
  onCompleted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  createSession: () => Promise<CheckoutSessionDto>;
  onCompleted: () => void;
}) {
  const [session, setSession] = useState<CheckoutSessionDto>();
  const [failed, setFailed] = useState(false);
  const {
    mountOn: frameTarget,
    status: frameStatus,
    error: frameError,
  } = useCheckout(open ? session : undefined, onCompleted);

  // One checkout per opening, even when effects run twice.
  const create = useEffectEvent(createSession);
  const requested = useRef(false);
  useEffect(() => {
    if (!open) {
      requested.current = false;
      return;
    }
    if (requested.current) return;
    requested.current = true;
    create().then(
      (next) => {
        setFailed(false);
        setSession(next);
      },
      () => setFailed(true),
    );
  }, [open]);

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="md" title={title} description={description}>
        {failed || frameError ? (
          <Well tone="danger" role="alert" title="The payment form didn’t load">
            {frameError ?? 'Try again in a moment.'}
          </Well>
        ) : (
          frameStatus === 'loading' && (
            <div aria-busy="true" className="flex flex-col gap-3">
              <span className="sr-only" role="status">
                Loading the payment form…
              </span>
              <Skeleton shape="rectangle" shimmer className="h-12" />
              <Skeleton shape="rectangle" shimmer className="h-40" />
            </div>
          )
        )}
        {/* The provider's checkout frame is mounted in here. */}
        <div
          ref={frameTarget}
          data-testid="checkout-frame"
          className={frameStatus === 'loading' ? 'h-0 overflow-hidden' : undefined}
        />
      </ModalContent>
    </Modal>
  );
}

function InvoiceBadge({ status }: { status: InvoiceDto['status'] }) {
  if (status === 'PAST_DUE') return <Badge tone="danger">Past due</Badge>;
  if (status === 'DUE') return <Badge tone="warning">Due</Badge>;
  return <Badge tone="success">Paid</Badge>;
}

const BRANDS: Record<string, string> = {
  american_express: 'American Express',
  diners_club: 'Diners Club',
  jcb: 'JCB',
  union_pay: 'UnionPay',
};

function paymentMethodLabel(method: PaymentMethodDto) {
  if (method.kind === 'PAYPAL') return 'PayPal';
  const brand = method.brand
    ? (BRANDS[method.brand] ??
      method.brand.charAt(0).toUpperCase() + method.brand.slice(1).replace(/_/g, ' '))
    : 'Card';
  return method.last4 ? `${brand} ending in ${method.last4}` : brand;
}

function paymentMethodDetail(method: PaymentMethodDto) {
  const parts = [];
  if (method.expiryMonth && method.expiryYear) {
    parts.push(
      `Expires ${String(method.expiryMonth).padStart(2, '0')}/${method.expiryYear}`,
    );
  }
  if (method.holderName) parts.push(method.holderName);
  return parts.join(' · ') || undefined;
}

function formatMoney(amount: number, currency: string) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount);
}

/**
 * Figma "Plan Card" (104:131) as Billing uses it: radius xl, 24px padding,
 * 20px gaps. The current plan has a neutral badge and a disabled CTA; the
 * recommended one a Deep Olive border, a solid badge and the primary CTA.
 */
function PlanCard({
  plan,
  yearly,
  current,
  recommended,
  cta,
  disabled,
  onChoose,
}: {
  plan: PlanDto;
  yearly: boolean;
  current: boolean;
  recommended: boolean;
  cta: string;
  disabled: boolean;
  onChoose: () => void;
}) {
  const id = useId();
  return (
    <article
      aria-labelledby={id}
      className={cn(
        'bg-bg-surface flex flex-1 flex-col gap-5 rounded-xl p-6',
        recommended
          ? 'border-brand-strong border-2 shadow-md'
          : 'border-border-subtle border',
      )}
    >
      <div className="flex items-center gap-2">
        <h3 id={id} className="text-heading-h4 text-text-primary flex-1">
          {plan.name}
        </h3>
        {current && <Badge>Current plan</Badge>}
        {recommended && (
          <Badge tone="brand" variant="solid">
            Recommended
          </Badge>
        )}
      </div>
      <p className="flex items-baseline gap-1.5">
        <span className="text-heading-h1 text-text-primary">
          ${yearly ? plan.yearlyPrice : plan.monthlyPrice}
        </span>
        <span className="text-body-sm text-text-secondary">
          {yearly ? 'per month, billed yearly' : 'per month'}
        </span>
      </p>
      <p className="text-body-sm text-text-secondary min-h-9">{plan.description}</p>
      <Button
        variant={recommended ? 'primary' : 'outline'}
        disabled={current || disabled}
        className="w-full"
        onClick={onChoose}
      >
        {cta}
      </Button>
      <div aria-hidden className="bg-border-subtle h-px" />
      <ul className="flex flex-col gap-2.5">
        {plan.features.map((feature) => (
          <li
            key={feature}
            className="text-body-sm text-text-primary flex items-center gap-2"
          >
            <Check aria-hidden className="text-icon-brand size-4 shrink-0" />
            {feature}
          </li>
        ))}
      </ul>
    </article>
  );
}
